import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { customersRepo } from '../repo/customersRepo'
import { logAudit } from '@/lib/audit'

export function useCustomers() {
  return useQuery({
    queryKey: ['customers'],
    queryFn: () => customersRepo.findAll(),
  })
}

export function useClientCustomers(clientId: string | undefined) {
  return useQuery({
    queryKey: ['customers', 'client', clientId],
    queryFn: () => clientId ? customersRepo.findByClient(clientId) : [],
    enabled: !!clientId,
  })
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: ['customers', id],
    queryFn: () => id ? customersRepo.findById(id) : null,
    enabled: !!id,
  })
}

export function useCreateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (customer: any) => customersRepo.create(customer),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      logAudit({
        action: 'create_customer',
        resource_type: 'customer',
        resource_id: data.id,
        client_id: data.client_id,
      })
    },
  })
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      customersRepo.update(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['customers', data.id] })
      logAudit({
        action: 'update_customer',
        resource_type: 'customer',
        resource_id: data.id,
        client_id: data.client_id,
      })
    },
  })
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => customersRepo.delete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['customers', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      logAudit({
        action: 'delete_customer',
        resource_type: 'customer',
        resource_id: id,
      })
    },
    onError: (error) => {
      console.error('useDeleteCustomer mutation error:', error)
    },
  })
}
